// ABOUTME: License purchase is charged and being fulfilled (DVLP-7860). Listens on the same
// ABOUTME: Stripe presence channel as the course checkout; fulfillment will not publish until this is subscribed.
import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Channel } from 'pusher-js';
import { Typography, emptyFragment } from '@execonline-inc/execonline-ui';
import ChannelAvailabilityStore from '../../../../../Socket/ChannelAvailabilityStore';
import PresenceChannel from '../../../../../Socket/PresenceChannel';
import { T } from '../../../../../Translations';
import { PaymentProcessing } from '../../ProgramExperienceEnrollment/ExperiencePaymentContent/Common';
import licensePurchaseStore from '../Store';
import { Processing } from '../Types';

const Binding = observer(function Binding({ channel }: { channel: Channel }) {
  useEffect(() => {
    channel.bind('succeeded', (body: unknown) => {
      licensePurchaseStore.messageReceived('succeeded', body);
    });
    channel.bind('processing', (body: unknown) => {
      licensePurchaseStore.messageReceived('processing', body);
    });
    channel.bind('server-error', (body: unknown) => {
      licensePurchaseStore.messageReceived('server-error', body);
    });
    return () => {
      channel.unbind_all();
    };
  }, [channel]);

  return emptyFragment();
});

const Listener = observer(function Listener({
  availability,
}: {
  availability: ChannelAvailabilityStore;
}) {
  switch (availability.channelAvailability.kind) {
    case 'available': {
      return <Binding channel={availability.channelAvailability.channel} />;
    }
    case 'not-available': {
      return emptyFragment();
    }
  }
});

interface Props {
  flow: Processing;
}

function ProcessingPurchase({ flow }: Props) {
  const availability = useMemo(() => new ChannelAvailabilityStore(), []);

  return (
    <div
      data-intercom-target="license-purchase-processing"
      className="flex w-full flex-col items-center justify-center px-6 py-24 text-center md:py-0"
    >
      <PresenceChannel
        name={`presence-stripe-payment-intent-${flow.stripePaymentIntentId}`}
        channelAvailabilityStore={availability}
      />
      <Listener availability={availability} />
      <PaymentProcessing />
      <Typography className="mt-9" color="black" variant="h5" as="p" fontWeight="medium">
        <T kind="Processing Your Payment" />
      </Typography>
      <Typography className="mt-8 text-gray-700" variant="bodyMedium" as="p">
        <T
          kind="Please do <strong>not</strong> close this dialog or your browser tab."
          strong={(value) => (
            <Typography className="underline underline-offset-2" fontWeight="bold" as="span">
              {value}
            </Typography>
          )}
        />
      </Typography>
    </div>
  );
}

export default observer(ProcessingPurchase);
